<template>
  <div class="card modal-card is-min-width-500">
    <header class="bandeau-modal card-header"></header>
    <div class="center">
      <div class="columns is-gapless">
        <div class="column mgt-20px">
          <h3 class="title is-size-3">
            {{ $t('editionCommande.logistiqueImpression.popUpMiseAjour.title') }}
          </h3>
        </div>
      </div>
    </div>
    <div class="modal-card-body">
      <error-msg-component
        :error="error"
        :messages="messages"
        @close="error = $event"
        title="general.problemOccurred"
      />
      <div class="card-content">
        <div class="columns is-vcentered">
          <b-field class="column is-narrow has-text-centered"
            :label="$t('editionCommande.logistiqueImpression.popUpMiseAjour.priseEnCompte')">
          </b-field>
        </div>
        <div
          v-for="(info, index) in infosData" :key="index"
          class="columns is-vcentered"
          :class="{'has-background-info-light': info.priseEnCompte }">
          <b-field class=" column is-1 has-text-centered">
            <qd-switch class="is-switch-centered" v-model="info.priseEnCompte">
            </qd-switch>
          </b-field>
          <b-field class="column is-3 pdl-40px"
            :label="$t(info.label)">
          </b-field>
          <b-field class="column is-8">
            <produit-autocomplete
              v-if="info.type === 'produitAutocomplete'"
              @input="updateInfo($event, info.field)"
              :societeActiveId="societeActiveId"
              :sens="constantes.SENS_ACHAT"
              :categories="[constantes.CATEGORIE_IMPRESSION_AFFICHE]"
              :filteredItemFunc="(produit) => produit.id !== info.value"
              expanded
            />
            <tiers-autocomplete
              v-else-if="info.type === 'tiersAutocomplete'"
              v-model="info.tiers"
              @input="updateInfo($event, info.field)"
              :societeActiveId="societeActiveId"
              :type="constantes.TIERS_TYPE_FOURNISSEUR"
              :sousTypes="info.sousTypes"
              :filteredItemFunc="(tiers) => tiers.id !== info.value"
              :title-pop-up-advanced-search="`${baseTrad}.advancedSearchFournisseur`"
              :tiers-options="info.tiersInIntervenantsCommande"
              :append-to-body="null"
              search-by-competence-fournisseur
              advanced-search
              expanded>
              <template slot-scope="{ option }">
                <p>
                  <span v-if="option.code">{{ option.code }} </span>
                  <span>{{ option.nomCommercial }}</span>
                </p>
                <p class="is-size-8 is-italic">
                  <span v-if="option.compteComptable">{{ option.compteComptable }} </span>
                  <span>{{ option.adressePostaleLibelle }}</span>
                </p>
              </template>
            </tiers-autocomplete>
            <b-datepicker
              v-else-if="info.type === 'date'"
              show-week-number
              v-model="info.value"
              icon="calendar-today"
              nearby-selectable-month-days
              append-to-body
              :editable="true"
              @input="info.priseEnCompte = true">
            </b-datepicker>
          </b-field>
        </div>
      </div>
      <footer class="buttons mgt-20px">
        <qd-button
          @click="$parent.close()"
          type="is-light"
          class="btn-modal-annuler">
          {{$t('general.button.cancel')}}
        </qd-button>
        <qd-button
          @click="applyUpdates"
          :disabled="!hasSomethingToApply"
          class="button is-addOrEdit">
          {{$t('general.button.appliquer')}}
        </qd-button>
      </footer>

    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import ProduitAutocomplete from '@/components/general/autocomplete/produitAutocomplete';
import TiersAutocomplete from '@/components/general/autocomplete/tiersAutocomplete';
import { CATEGORIE_IMPRESSION_AFFICHE } from '@/configuration/catalogue/produitConf';
import {
  TIERS_TYPE_FOURNISSEUR,
  TIERS_SOUS_TYPE_LIEU_DE_LIVRAISON,
  LISTE_SOUS_TYPE_FOURNISSEURS_PAGES,
} from '@/configuration/tiers/tiersConf';
import { SENS_ACHAT } from '@/configuration/devisConf';
import formatDateIso from '@/services/general/formatIsoDate';
import errorMsgComponent from '@/components/general/errorMsgComponent';
import { updateBesoinFields } from '@/api/besoin';
import validation from '@/services/bodac/besoin/validation';
import messageErreur from '@/services/general/messageErreur';
import messageSuccess from '@/services/general/messageSucces';

const Societe = createNamespacedHelpers('societe');

export default {
  name: 'PopupUpdateBesoinInfos',
  props: {
    besoinIds: {
      type: Array,
      required: true,
    },
    tiersInIntervenantsCommande: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  components: {
    TiersAutocomplete,
    ProduitAutocomplete,
    errorMsgComponent,
  },
  data() {
    return {
      messages: [],
      error: false,
      infosData: [
        {
          field: 'produitAchatId',
          value: null,
          label: 'editionCommande.logistiqueImpression.popUpMiseAjour.produitAchat',
          type: 'produitAutocomplete',
          priseEnCompte: false,
        },
        {
          field: 'tiersDestinataireId',
          value: null,
          label: 'editionCommande.logistiqueImpression.popUpMiseAjour.tiersDestinataire',
          type: 'tiersAutocomplete',
          priseEnCompte: false,
          sousTypes: LISTE_SOUS_TYPE_FOURNISSEURS_PAGES
            .map(el => el.code)
            .filter(el => el !== TIERS_SOUS_TYPE_LIEU_DE_LIVRAISON),
          tiersInIntervenantsCommande: this.tiersInIntervenantsCommande,
          tiers: {},
        },
        {
          field: 'lieuLivraisonPrestationId',
          value: null,
          label: 'editionCommande.logistiqueImpression.popUpMiseAjour.lieuLivraisonPrestation',
          type: 'tiersAutocomplete',
          priseEnCompte: false,
          sousTypes: [TIERS_SOUS_TYPE_LIEU_DE_LIVRAISON],
          tiersInIntervenantsCommande: [],
          tiers: {},
        },
        {
          field: 'dateLivraison',
          value: null,
          label: 'editionCommande.logistiqueImpression.popUpMiseAjour.dateLivraison',
          type: 'date',
          priseEnCompte: false,
        },
      ],
      constantes: {
        SENS_ACHAT,
        TIERS_TYPE_FOURNISSEUR,
        CATEGORIE_IMPRESSION_AFFICHE,
        TIERS_SOUS_TYPE_LIEU_DE_LIVRAISON,
      },
      baseTrad: 'editionCommande.logistiqueImpression.affiches',
    };
  },
  computed: {
    ...Societe.mapGetters({
      societeActiveId: 'societeActiveId',
    }),
    hasSomethingToApply() {
      return this.infosData.some(info => info.priseEnCompte);
    },
  },
  methods: {
    updateInfo(data, field) {
      if (!data) return;
      const info = this.infosData.find(info => info.field === field);
      if (info) {
        info.value = data?.id;
        info.priseEnCompte = !!info.value;
      }
    },
    async applyUpdates() {
      const loadingComponent = this.$buefy.loading.open();
      if (!this.hasSomethingToApply) return;
      this.error = false;
      this.messages = [];
      try {
        const data = this.infosData
          .filter(info => info.priseEnCompte)
          .map(info => ({
            field: info.field,
            value: info.type === 'date' ? formatDateIso(info.value) : info.value,
          }));
        await updateBesoinFields({
          ids: this.besoinIds,
          besoinFields: data,
        });
        messageSuccess(this.$i18n.t('editionCommande.logistiqueImpression.popUpMiseAjour.success'));
        this.$parent.close();
        this.$emit('updateDone');
      } catch (e) {
        if (e?.response?.data?.error) {
          validation(e.response, this.messages);
          this.error = true;
          return;
        }
        messageErreur(this.$i18n);
        throw e;
      } finally {
        loadingComponent.close();
      }
    },
  },
};
</script>
